import { Header, HeaderBreadcrumb } from 'common/ui/Header';
import React, { useState, useMemo } from 'react';
import { Stack, TablePagination, Typography } from '@mui/material';
import { VerticalTabItem, VerticalTabs } from 'common/ui';
import { AnalysingLoadingGif } from 'common/animations/AnalysingLoadingGif';
import { SourcingSharedPreviewInvitation } from 'modules/sourcing-shared/components/SourcingSharedPreviewInvitation';
import { ToastType, useToast } from 'services/toast';
import { Button } from 'system/atoms';
import { SourcingSharedCandidateBatch } from 'modules/sourcing-shared/shared-stores/types';
import {
  SourcingSharedCustomizeMessageEmailFormData,
  SourcingSharedCustomizeMessageSmsFormData,
  SourcingSharedSetupApplicationFormData
} from 'modules/sourcing-shared/forms';
import { type CampaignCreationSharedJobInformationFormData } from 'modules/campaign-creation-shared/forms';
import { SearchPeopleProfile } from 'modules/sourcing-external/types/SearchPeopleQueryItem';
import { useSourcingSharedFillSubjectLine } from 'modules/sourcing-shared/hooks/useSourcingSharedFillSubjectLine';
import { useSourcingSharedFillSubjectMessage } from '../hooks/useSourcingSharedFillSubjectMessage';
import { useDrawerV2 } from 'common/drawer/hooks/useDrawerV2';
import { SourcingSharedEditInvitationDrawer } from '../components/SourcingSharedEditInvitationDrawer';
import { Channels } from 'generated-graphql/types';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { resolveOrganizationDisplayName } from 'modules/organization/utils/resolveOrganizationDisplayName';

const CURRENT_BREADCRUMB_TITLE = 'Review invitations';

export type ReviewInvitationsCandidateItem = {
  id: string;
  fullName: string;
  location: string;
  title: string;
  customInvitationMessage: string;
  profile?: SearchPeopleProfile;
};

type SourcingSharedReviewInvitationsStepProps = {
  headerBreadcrumbs: HeaderBreadcrumb[];
  candidateItems: ReviewInvitationsCandidateItem[];
  selectedCandidateId: string;
  candidateBatch: SourcingSharedCandidateBatch;
  channel: Channels;
  customizeMessageFormData:
    | SourcingSharedCustomizeMessageEmailFormData
    | SourcingSharedCustomizeMessageSmsFormData;
  jobInformationFormData: CampaignCreationSharedJobInformationFormData;
  applicationFormData: SourcingSharedSetupApplicationFormData;
  editedOpeningMessage: string | null;
  setSelectedCandidateId: (candidateId: string) => void;
  onReviewInvitationsBack: () => void;
  onInviteSubmit: () => Promise<void>;
  onUpdateInvitationMessage: (
    candidateId: string,
    customMessage: string
  ) => Promise<void>;
  onGenerateRelevance: (
    candidateId: string,
    candidateBatch: SourcingSharedCandidateBatch,
    customizeMessageFormData:
      | SourcingSharedCustomizeMessageEmailFormData
      | SourcingSharedCustomizeMessageSmsFormData
  ) => Promise<string>;
  isGeneratingRelevance: boolean;
};

export const SourcingSharedReviewInvitationsStep: React.FC<
  SourcingSharedReviewInvitationsStepProps
> = ({
  headerBreadcrumbs,
  candidateItems,
  selectedCandidateId,
  candidateBatch,
  customizeMessageFormData,
  jobInformationFormData,
  applicationFormData,
  editedOpeningMessage,
  setSelectedCandidateId,
  onReviewInvitationsBack,
  onInviteSubmit,
  onUpdateInvitationMessage,
  onGenerateRelevance,
  isGeneratingRelevance,
  channel
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { openDrawer } = useDrawerV2();
  const organizationName = resolveOrganizationDisplayName(useOrganization());

  const memoizedCustomizeMessageFormData = useMemo(
    () => ({
      ...customizeMessageFormData,
      customTemplatedMessage:
        editedOpeningMessage || customizeMessageFormData?.customTemplatedMessage
    }),
    [customizeMessageFormData, editedOpeningMessage]
  );

  const jobPostingUrl = useMemo(
    () =>
      candidateBatch?.atsJobPostingUrl ||
      applicationFormData?.customJobPostingUrl ||
      candidateBatch?.customJobPostingUrl ||
      '',
    [
      candidateBatch?.atsJobPostingUrl,
      candidateBatch?.customJobPostingUrl,
      applicationFormData?.customJobPostingUrl
    ]
  );

  // Compute selected candidate index early, protecting against -1 from findIndex
  const selectedCandidateIndex = Math.max(
    0,
    candidateItems.findIndex((item) => item.id === selectedCandidateId)
  );
  const selectedCandidate = candidateItems[selectedCandidateIndex];

  const { filledSubjectLine } = useSourcingSharedFillSubjectLine({
    invitationCandidateItem: selectedCandidate,
    customizeMessageFormData: memoizedCustomizeMessageFormData,
    jobInformationFormData,
    jobPostingUrl,
    organizationName
  });

  const { filledSubjectMessage } = useSourcingSharedFillSubjectMessage({
    invitationCandidateItem: selectedCandidate,
    candidateBatch,
    customizeMessageFormData: memoizedCustomizeMessageFormData,
    jobInformationFormData,
    jobPostingUrl,
    organizationName,
    onGenerateRelevance
  });

  if (candidateItems.length === 0) {
    return null;
  }

  const items = candidateItems.map((item) => ({
    id: item.id,
    title: item.fullName,
    subtitle: item.location,
    description: item.title
  }));

  const changePage = (page: number) => {
    const candidate = items[page];

    if (!candidate) {
      return;
    }

    setSelectedCandidateId(candidate.id);
  };

  const onTabChange = (item: VerticalTabItem) => {
    const candidate = candidateItems.find((i) => i.id === item.id);

    if (!candidate) {
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });

    setSelectedCandidateId(candidate.id);
  };

  const handleSendInvitations = async () => {
    try {
      setIsLoading(true);
      await onInviteSubmit();
    } catch (error) {
      showToast({
        message: 'Failed to send invitations',
        type: ToastType.Error
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditInvitation = () => {
    openDrawer({
      width: '900px',
      children: (
        <SourcingSharedEditInvitationDrawer
          channel={channel}
          candidate={selectedCandidate}
          filledMessage={filledSubjectMessage}
          filledSubject={filledSubjectLine}
          onUpdateInvitationMessage={onUpdateInvitationMessage}
        />
      )
    });
  };

  return (
    <>
      <Header
        title={CURRENT_BREADCRUMB_TITLE}
        breadcrumbs={headerBreadcrumbs}
        currentBreadcrumb={CURRENT_BREADCRUMB_TITLE}
        onBack={onReviewInvitationsBack}
      />

      <Stack direction="row" spacing="50px">
        <VerticalTabs
          defaultSelectedIndex={selectedCandidateIndex}
          items={items}
          emptyMessage="No candidates selected"
          onTabChange={onTabChange}
        />

        <Stack direction="column" sx={{ width: '600px' }} gap="30px">
          {isGeneratingRelevance && (
            <Stack direction="row" alignItems="center" gap={1}>
              <AnalysingLoadingGif />
              <Typography fontWeight={600} fontSize="24px">
                Generating personalised message
              </Typography>
            </Stack>
          )}
          {!isGeneratingRelevance && (
            <>
              <SourcingSharedPreviewInvitation
                candidate={selectedCandidate}
                customizeMessageFormData={memoizedCustomizeMessageFormData}
                filledMessage={filledSubjectMessage}
                filledSubject={filledSubjectLine}
              />
              <Stack direction="row" spacing={2}>
                <Button
                  variant="primary"
                  onClick={handleSendInvitations}
                  loading={isLoading}
                >
                  Send {candidateItems.length} invitation
                  {candidateItems.length > 1 ? 's' : ''} now
                </Button>
                <Button variant="secondary" onClick={handleEditInvitation}>
                  Edit {selectedCandidate.fullName.split(' ')[0]}'s invitation
                </Button>
              </Stack>
            </>
          )}
        </Stack>
        <TablePagination
          sx={{
            mb: 0,
            pb: 0,
            '& .MuiTablePagination-actions .MuiIconButton-root.Mui-disabled': {
              color: 'grey.400'
            }
          }}
          component="div"
          labelDisplayedRows={({ from, count }) => {
            return `${from} of ${count}`;
          }}
          count={items.length}
          page={selectedCandidateIndex}
          rowsPerPage={1}
          rowsPerPageOptions={[]}
          onPageChange={(_event, page) => {
            changePage(page);
          }}
        />
      </Stack>
    </>
  );
};
